// frontend/my-react-app/src/DailyNotes.jsx
// Updated: October 13, 2025 - Added account switching support
// Previous update: September 2025 - Connected to backend API

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePet } from './contexts/PetContext';
import { fetchDailyNote, updateDailyNote } from './services/api';
import './DailyNotes.css';

const DailyNotes = ({ date = new Date() }) => {
  const { getToken } = useAuth();
  const { selectedPet } = usePet();
  const [noteText, setNoteText] = useState('');
  const [lastSavedText, setLastSavedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const notePetIdRef = useRef(null);
  const previousDateRef = useRef(null); // Track date changes for navigation warning
  const noteTextRef = useRef(''); // Ref to track current noteText
  const lastSavedTextRef = useRef(''); // Ref to track last saved text
  
  const MAX_CHARACTERS = 500;
  const charactersUsed = noteText.length;
  const charactersRemaining = MAX_CHARACTERS - charactersUsed;
  
  // Check if there are unsaved changes
  const hasUnsavedChanges = noteText !== lastSavedText;

  // Convert date to string format for API (YYYY-MM-DD)
  const getDateString = (dateObj) => {
    if (typeof dateObj === 'string') {
      // If already a string, return as-is
      return dateObj;
    }
    return dateObj.toISOString().split('T')[0];
  };

  // Update refs when state changes (for warning logic)
  useEffect(() => {
    noteTextRef.current = noteText;
  }, [noteText]);
  
  useEffect(() => {
    lastSavedTextRef.current = lastSavedText;
  }, [lastSavedText]);
  
  useEffect(() => {
    if (!selectedPet) return;

    const currentDate = getDateString(date);
    const dateChanged = previousDateRef.current !== null && previousDateRef.current !== currentDate;

    if (dateChanged && noteTextRef.current !== lastSavedTextRef.current) {
      window.alert('You have unsaved changes. They will be lost if you navigate away.');
    }

    previousDateRef.current = currentDate;

    const fetchNote = async () => {
      setIsLoading(true);
      const dateString = getDateString(date);
      try {
        const data = await fetchDailyNote(dateString, getToken, selectedPet.id);
        const savedNote = data?.note ?? '';
        setNoteText(savedNote);
        setLastSavedText(savedNote);
        noteTextRef.current = savedNote;
        lastSavedTextRef.current = savedNote;
        notePetIdRef.current = selectedPet.id;
      } catch (error) {
        console.error('Error fetching note:', error);
        setNoteText('');
        setLastSavedText('');
        noteTextRef.current = '';
        lastSavedTextRef.current = '';
        notePetIdRef.current = selectedPet.id;
      } finally {
        setIsLoading(false);
      }
    };

    fetchNote();
  }, [date, selectedPet, getToken]);
  
  const handleSave = async () => {
    if (!selectedPet || !notePetIdRef.current) return;
    if (noteText.length > MAX_CHARACTERS) {
      alert('Note is too long. Please reduce the length.');
      return;
    }
    
    setIsSaving(true);
    const dateString = getDateString(date);
    
    try {
      await updateDailyNote(dateString, noteText, getToken, notePetIdRef.current);
      setLastSavedText(noteText); // Update saved text
      lastSavedTextRef.current = noteText; // Update ref
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    // Only update if within character limit
    if (newText.length <= MAX_CHARACTERS) {
      setNoteText(newText);
    }
  };

  // Format status text (saved time or unsaved changes)
  const getStatusText = () => {
    if (isLoading) return 'Loading...';
    if (isSaving) return 'Saving...';
    if (hasUnsavedChanges) return 'Unsaved changes';
    if (lastSaved) {
      const now = new Date();
      const diffInSeconds = Math.floor((now - lastSaved) / 1000);
      
      if (diffInSeconds < 5) return 'Saved just now';
      if (diffInSeconds < 60) return `Saved ${diffInSeconds} seconds ago`;
      
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes === 1) return 'Saved 1 minute ago';
      if (diffInMinutes < 60) return `Saved ${diffInMinutes} minutes ago`;
      
      return 'Saved';
    }
    return '';
  };

  return (
    <div className="daily-notes-section">
      <div className="notes-header">
        <h2 className="notes-title">Today's Notes</h2>
        <span className="notes-subtitle">
          Add anything you'd like to record - how you're feeling, why you skipped a medication, etc.
        </span>
      </div>
      
      <div className="notes-content">
        <textarea
          className="notes-textarea"
          placeholder="Add notes here..."
          value={noteText}
          onChange={handleTextChange}
          rows={4}
          disabled={isLoading}
        />
        
        <div className="notes-footer">
          <span className={`character-count ${charactersRemaining < 50 ? 'warning' : ''} ${charactersRemaining < 0 ? 'error' : ''}`}>
            {charactersRemaining} characters remaining
          </span>
          <div className="notes-footer-right">
            <span className={`save-status ${hasUnsavedChanges ? 'unsaved' : ''}`}>
              {getStatusText()}
            </span>
            <button
              className="save-button"
              onClick={handleSave}
              disabled={isLoading || isSaving || !hasUnsavedChanges}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyNotes;